import RightArrow from "assets/images/v2/right-arrow.svg";
import tasksChecklistAnimation from "assets/images/v2/tasks-checklist.gif";
import TogglePills, { ToggleBadgeVariant } from "components/v2/toggle-pills/TogglePills";
import "./Tasks.scss";
import { useSelector, useDispatch } from "react-redux";
import {
  getSelectedTaskId,
  setSelectedTaskId,
  getTaskFilter,
  setTaskFilter,
  TaskFilterType,
  getCurrentUserName,
  getProspectId,
  getLoanNumber,
  getLoanId,
  getDealId,
  getEncompassLoanId,
} from "features/authSlice";
import {
  getSessionHeldUnderReviewIds,
  addSessionHeldUnderReviewId,
  removeSessionHeldUnderReviewId, 
  getLoanProgress
} from "pages/dashboard/store/dashboardSlice";
import { trackFSDocumentsUploaded, trackFSGuidedExperienceFirstTaskCompletedIfEligible } from "utilities/full-story";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NeedItem, NeedStatus, Document, NeedType } from "../../dashboardService";
import FileUploadePanel from "components/v2/file-upload-panel/FileUploadPanel";
import { Spinner } from "components/v2/spinner/spinner";
import useDesktopChecker from "hooks/useDesktopChecker";
import { useESignFSTracking } from "hooks/useESignFSTracking";
import { useNeedsRefetchUntil } from "hooks/useNeedsRefetchUntil";
import { useGetFeatureFlagsQuery } from "services/common/featureFlagService";
import useTabletChecker from "hooks/useTabletChecker";
import { LoadingPageProps } from "pages/dashboard/v2/loadingPageProps";
import { TASK_CARD_TITLE, TASK_CARD_HEADING_TEXT, TASK_CARD_HEADING_UNDER_REVIEW_TEXT, TASK_CARD_HEADING_COMPLETE_TEXT, TASK_ERROR_ESIGN_TITLE, TASK_ERROR_TITLE_DOCUMENT_UNAVAILABLE, TASK_ERROR_DOCUMENT_CARD_TITLE, TASK_ERROR_CARD_DESCRIPTION_DESKTOP, TASK_ERROR_ESIGN_DESCRIPTION, TASK_ERROR_CARD_DESCRIPTION_MOBILE, TASK_ERROR_DOCUMENT_DESCRIPTION, TASK_ERROR_SUPPORT, TASK_ERROR_TITLE_UNDER_REVIEW, TASK_ERROR_REVIEW_DESCRIPTION, TASK_ERROR_DETAILS_TEMPORARILY_UNAVAILABLE, TASK_NEEDS_LOAD_ERROR_TITLE, TASK_NEEDS_LOAD_ERROR_DESCRIPTION, TASK_STATUS_REFRESH_ERROR, TASK_ERROR_COMPLETE_ESIGN_TITLE, TASK_ERROR_COMPLETE_ESIGN_LEFT_DESCRIPTION, TASK_ERROR_COMPLETE_ESIGN_RIGHT_DESCRIPTION, TASK_ERROR_COMPLETE_DOCUMENT_TITLE, TASK_ERROR_COMPLETE_DOCUMENT_LEFT_DESCRIPTION, TASK_ERROR_COMPLETE_DOCUMENT_RIGHT_DESCRIPTION } from "utilities/Constant";
import Pill from "components/v2/pill/pill";
import NafInsuranceChip from "components/v2/naf-insurance-chip/NafInsuranceChip";
import { isNonEmptyString } from "utilities/string";
import ESignPanel from "pages/dashboard/home/v2/eSign-panel/ESignPanel";
import Verification from "pages/dashboard/v2/verification-post-app/verification/Verification";
import { VerificationType } from "pages/online-verification/verificationService";
import {
  getTaskStatus,
  getEffectiveTaskStatus,
  getEffectiveTaskStatusForCount,
  isNeedCompleted,
  shouldDisplayNafInsuranceTaskCta,
} from "utilities/needsHelper";
import ErrorRedIcon from "assets/images/v2/Error.svg";
import ClockIcon from "assets/images/v2/clock.svg";
import SuccessCheckIcon from "assets/images/v2/Success-Check.svg";
import chevronLeft from "assets/images/v2/chevron-left.svg";
import errorWindow from "assets/images/v2/error-window.svg";
import errorComputer from "assets/images/v2/error-computer.svg";
import errorConnector from "assets/images/v2/error-connector.svg";
import errorStand from "assets/images/v2/error-stand.svg";
import errorIconCircle from "assets/images/v2/error-icon-circle.svg";
import errorIconExclamation from "assets/images/v2/error-icon-exclamation.svg";
import ToastMessage from "components/ui/toast-message/ToastMessage";
import WireFraudPanel from "pages/dashboard/home/v2/wire-fraud-panel/WireFraudPanel";
import {
  filterWireFraudDisplayTasks,
  getTaskDisplayTitle,
  isWireFraudTask,
  WIRE_FRAUD_ACKNOWLEDGED_MESSAGE,
  WIRE_FRAUD_TASK_DESCRIPTION,
  WIRE_FRAUD_TASK_UNAVAILABLE_DESCRIPTION,
  WIRE_FRAUD_TASK_UNAVAILABLE_TITLE,
} from "utilities/wireFraud";

const WireFraudAcknowledgedToast = memo(function WireFraudAcknowledgedToast() {
  return (
    <ToastMessage
      message={WIRE_FRAUD_ACKNOWLEDGED_MESSAGE}
      state="success"
      timeout={3000}
      position="top-right"
    />
  );
});

const NEEDS_ESIGN_REFETCH_DELAY_MS = 3000;
const MAX_NEED_STATUS_REFETCH_ATTEMPTS = 5;
const EMPTY_HELD_UNDER_REVIEW_IDS: string[] = [];

const isNeedStillInTodoTab = (item: NeedItem | undefined): boolean =>
  !!item && getTaskStatus(item) === TaskFilterType.ToDo;

interface ActiveTask {
  needId: string;
  title: string;
  subTitle?: string;
  assignedTo?: string;
  note?: string;
  documents?: Document[];
  needType?: NeedType;
  needStatus?: NeedStatus;
  lat?: string;
  packageId?: string;
  isEnabled?: boolean;
  prospectId?: string;
  isEsignStartedFSEventSent?: boolean;
  taskLoadErrorType?: string;
  isDefault?: boolean;
  needRequestTypeId?: string;
  shouldShowInsuranceCta?: boolean;
}

interface TaskProps {
  title: string;
  subTitle: string;
  description?: string;
  isSelected?: boolean;
  isComments?: boolean;
  isError?: boolean;
  needType?: NeedType;
  needStatus?: NeedStatus;
  isDefault?: boolean;
  shouldShowInsuranceCta?: boolean;
  handleClick?: () => void;
}

const isCompletedStatus = (needStatus?: NeedStatus): boolean => {
  const statusLower = needStatus?.toLowerCase();
  return statusLower === NeedStatus.Completed.toLowerCase() || 
         statusLower === NeedStatus.Satisfied.toLowerCase();
};

const getErrorTitle = (needType?: NeedType, needStatus?: NeedStatus, isDesktop?: boolean, isDefault?: boolean): string => {
  if (isDefault) {
    if (needType === NeedType.ESign) return TASK_ERROR_ESIGN_TITLE;
    return TASK_ERROR_DOCUMENT_CARD_TITLE;
  }
  
  // Other error tasks respect their actual status
  if (isCompletedStatus(needStatus)) {
    return needType === NeedType.ESign ? TASK_ERROR_COMPLETE_ESIGN_TITLE : TASK_ERROR_COMPLETE_DOCUMENT_TITLE;
  }
  if (needStatus?.toLowerCase() === NeedStatus.ReviewNeeded.toLowerCase()) {
    return isDesktop ? TASK_ERROR_TITLE_DOCUMENT_UNAVAILABLE : TASK_ERROR_TITLE_UNDER_REVIEW;
  }
  if (needType === NeedType.ESign) return TASK_ERROR_ESIGN_TITLE;
  if (needType === NeedType.WireFraud) return WIRE_FRAUD_TASK_UNAVAILABLE_TITLE;
  return TASK_ERROR_DOCUMENT_CARD_TITLE;
};


const getErrorDescription = (needType?: NeedType, needStatus?: NeedStatus, isDesktop?: boolean, isDefault?: boolean): string => {
  if (isDefault) {
    return isDesktop ? TASK_ERROR_CARD_DESCRIPTION_DESKTOP : TASK_ERROR_CARD_DESCRIPTION_MOBILE;
  }
  
  // Other error tasks respect their actual status
  if (isCompletedStatus(needStatus)) {
    if (!isDesktop) {
      return TASK_ERROR_CARD_DESCRIPTION_MOBILE;
    }
    if (needType === NeedType.ESign) {
      return TASK_ERROR_COMPLETE_ESIGN_LEFT_DESCRIPTION;
    }
    return TASK_ERROR_COMPLETE_DOCUMENT_LEFT_DESCRIPTION;
  }
  if (needStatus?.toLowerCase() === NeedStatus.ReviewNeeded.toLowerCase()) {
    return isDesktop ? TASK_ERROR_DETAILS_TEMPORARILY_UNAVAILABLE : TASK_ERROR_CARD_DESCRIPTION_MOBILE;
  }
  if (needType === NeedType.WireFraud) {
    return isDesktop
      ? TASK_ERROR_CARD_DESCRIPTION_DESKTOP
      : `${WIRE_FRAUD_TASK_UNAVAILABLE_DESCRIPTION}\n ${TASK_ERROR_SUPPORT}`;
  }
  return isDesktop ? TASK_ERROR_CARD_DESCRIPTION_DESKTOP : TASK_ERROR_CARD_DESCRIPTION_MOBILE;
};

const isTaskReviewNeeded = (task: ActiveTask | undefined): boolean =>
  !task?.isDefault && task?.needStatus?.toLowerCase() === NeedStatus.ReviewNeeded.toLowerCase();

const isTaskCompletedErrorState = (task: ActiveTask | undefined): boolean =>
  !task?.isDefault && isCompletedStatus(task?.needStatus as NeedStatus);

const getTaskErrorIconSrc = (isCompletedError?: boolean, isUnderReviewError?: boolean): string => {
  if (isCompletedError) {
    return SuccessCheckIcon;
  }
  if (isUnderReviewError) {
    return ClockIcon;
  }
  return ErrorRedIcon;
};

const getTaskUnavailableModifierClass = (task: ActiveTask | undefined): string => {
  if (isTaskCompletedErrorState(task)) {
    return "tasks__task-unavailable--complete";
  }
  if (isTaskReviewNeeded(task)) {
    return "tasks__task-unavailable--review";
  }
  return "tasks__task-unavailable--todo";
};

const getNoTasksMessage = (taskFilter: TaskFilterType): string => {
  if (taskFilter === TaskFilterType.ToDo) {
    return "When you have tasks you will be able to view more details here.";
  }
  if (taskFilter === TaskFilterType.Complete) {
    return "Your completed tasks will show up here";
  }
  return "";
};

const getTaskCardHeadingText = (taskFilter: TaskFilterType): string => {
  if (taskFilter === TaskFilterType.UnderReview) {
    return TASK_CARD_HEADING_UNDER_REVIEW_TEXT;
  }
  if (taskFilter === TaskFilterType.Complete) {
    return TASK_CARD_HEADING_COMPLETE_TEXT;
  }
  return TASK_CARD_HEADING_TEXT;
};

const renderTaskUnavailableDetails = (task: ActiveTask) => {
  if (isTaskCompletedErrorState(task)) {
    const title =
      task.needType === NeedType.ESign
        ? TASK_ERROR_COMPLETE_ESIGN_TITLE
        : TASK_ERROR_COMPLETE_DOCUMENT_TITLE;
    const description =
      task.needType === NeedType.ESign
        ? TASK_ERROR_COMPLETE_ESIGN_RIGHT_DESCRIPTION
        : TASK_ERROR_COMPLETE_DOCUMENT_RIGHT_DESCRIPTION;

    return (
      <>
        <h5 className="tasks__task-unavailable-title paragraph-medium-medium">{title}</h5>
        <p className="tasks__task-unavailable-description paragraph-medium-regular">
          {description}
          <br />
          {TASK_ERROR_SUPPORT}
        </p>
      </>
    );
  }

  if (isTaskReviewNeeded(task)) {
    return (
      <>
        <h5 className="tasks__task-unavailable-title paragraph-medium-medium">
          {TASK_ERROR_TITLE_UNDER_REVIEW}
        </h5>
        <p className="tasks__task-unavailable-description paragraph-medium-regular">
          {TASK_ERROR_REVIEW_DESCRIPTION}
          <br />
          {TASK_ERROR_SUPPORT}
        </p>
      </>
    );
  }

  if (task.needType === NeedType.WireFraud) {
    return (
      <>
        <h5 className="tasks__task-unavailable-title paragraph-medium-medium">
          {WIRE_FRAUD_TASK_UNAVAILABLE_TITLE}
        </h5>
        <p className="tasks__task-unavailable-description paragraph-medium-regular">
          {WIRE_FRAUD_TASK_UNAVAILABLE_DESCRIPTION}
          <br />
          {TASK_ERROR_SUPPORT}
        </p>
      </>
    );
  }

  const title =
    task.needType === NeedType.ESign
      ? TASK_ERROR_ESIGN_TITLE
      : TASK_ERROR_TITLE_DOCUMENT_UNAVAILABLE;
  const description =
    task.needType === NeedType.ESign
      ? TASK_ERROR_ESIGN_DESCRIPTION
      : TASK_ERROR_DOCUMENT_DESCRIPTION;

  return (
    <>
      <h5 className="tasks__task-unavailable-title paragraph-medium-medium">{title}</h5>
      <p className="tasks__task-unavailable-description paragraph-medium-regular">
        {description}
        <br />
        {TASK_ERROR_SUPPORT}
      </p>
    </>
  );
};

const Task = (props: TaskProps) => {
  const { title, subTitle, description, handleClick, isSelected, isComments, isError, needType, needStatus, isDefault, shouldShowInsuranceCta } = props;
  const isDesktop = useDesktopChecker();
  const isTablet = useTabletChecker();
  const isUnderReviewError = isError && !isDefault && needStatus?.toLowerCase() === NeedStatus.ReviewNeeded.toLowerCase();
  const isCompletedError = isError && !isDefault && isCompletedStatus(needStatus);
  const isTodoError = isError && !isUnderReviewError && !isCompletedError;
  const isClickable = isCompletedError 
    ? (isDesktop || isTablet) 
    : (isDesktop || isTablet) || !isError;
  
  const classNames = [
    "task-item",
    isSelected && (isDesktop || isTablet || isError) ? "selected" : "",
    isTodoError ? "has-error-todo" : "",
    isUnderReviewError ? "has-error-review" : "",
    isCompletedError ? "has-error-complete" : "",
  ]
    .filter(Boolean)
    .join(" ");
  const showSelectedStyle = Boolean(isSelected && (isDesktop || isTablet || isError));

  return (
    <button
      className={classNames}
      onClick={isClickable ? handleClick : undefined}
      aria-current={showSelectedStyle ? "true" : undefined}
    >
      <div className="task-item__text">
        {isError && needType !== NeedType.WireFraud ? (
          <>
            <div className="task-item__error-header">
              <img 
                src={getTaskErrorIconSrc(isCompletedError, isUnderReviewError)} 
                alt="" 
                aria-hidden="true" 
                className="task-item__error-icon" 
              />
              <div className="task-item__title paragraph-medium-medium">{getErrorTitle(needType, needStatus, isDesktop || isTablet, isDefault)}</div>
            </div>
            <div className="task-item__error-description paragraph-small-regular">
              {getErrorDescription(needType, needStatus, isDesktop || isTablet, isDefault)}
            </div>
          </>
        ) : (
          <>
            <div className="task-item__title paragraph-medium-medium">{title}</div>
            {isNonEmptyString(description) && (
              <div className="task-item__description paragraph-small-regular">{description}</div>
            )}
            <div className="task-item__pills-container">
              {isNonEmptyString(subTitle) && (
                <div className="task-item__subtitle">
                  <Pill text={subTitle} variant="assigned" />
                </div>
              )}
              {isComments && (
                <div className="task-item__comments">
                  <Pill text="New Comments" variant="comments" />
                </div>
              )}
              {shouldShowInsuranceCta && <NafInsuranceChip />}
            </div>
          </>
        )}
      </div>
      {!isDesktop && !isError && <img src={RightArrow} alt="Right arrow" />}
    </button>
  );
};

export interface TasksPageProps extends LoadingPageProps {
  isNeedsListLoading: boolean;
  isNeedsListFetching: boolean;
  isNeedsListError?: boolean;
  needsList: NeedItem[];
  refetchNeedsList?: () => Promise<{ data?: NeedItem[] } | undefined>;
}

const Tasks: React.FC<TasksPageProps> = ({
  onLoad,
  isNeedsListLoading,
  isNeedsListFetching,
  isNeedsListError = false,
  needsList = [],
  refetchNeedsList,
}: TasksPageProps) => {
  const isDesktop = useDesktopChecker();
  const isTablet = useTabletChecker();
  const [showUpload, setShowUpload] = useState(false);
  const [isMobileSubmitSuccess, setIsMobileSubmitSuccess] = useState(false);
  const [showVerificationDrawer, setShowVerificationDrawer] = useState(true);
  const [taskFilters, setTaskFilters] = useState<{
    label: string;
    value: TaskFilterType;
    badge?: string;
    badgeVariant?: ToggleBadgeVariant;
  }[]>([
    { label: "To Do", value: TaskFilterType.ToDo, badgeVariant: "alert" },
    { label: "Under Review", value: TaskFilterType.UnderReview, badgeVariant: "warning" },
    { label: "Complete", value: TaskFilterType.Complete, badgeVariant: "success" },
  ]);

  const [taskVOASimulation, setTaskVOASimulation] = useState<Partial<ActiveTask>>({});

  const selectedTaskIdFromRedux = useSelector(getSelectedTaskId);
  const taskFilter = useSelector(getTaskFilter);
  const dispatch = useDispatch();
  const username = useSelector(getCurrentUserName);
  const prospectId = useSelector(getProspectId);
  const dealId = useSelector(getDealId);
  const loanNumber = useSelector(getLoanNumber);
  const loanId = useSelector(getLoanId);
  const losId = useSelector(getEncompassLoanId);
  const loanProgress = useSelector(getLoanProgress);
  const hasInitializedRef = useRef(false);
  const selectedDetailRef = useRef<HTMLDivElement>(null);
  const sessionHeldUnderReviewIds = useSelector(getSessionHeldUnderReviewIds);
  const { data: featureFlag } = useGetFeatureFlagsQuery();
  const isEnhancedConditionsReviewSessionEnabled = featureFlag?.enhancedConditionsSessionHeldUnderReviewEnabled === true;
  const effectiveHeldUnderReviewIds = isEnhancedConditionsReviewSessionEnabled ? sessionHeldUnderReviewIds : EMPTY_HELD_UNDER_REVIEW_IDS;
  const displayNeedsList = useMemo(
    () => filterWireFraudDisplayTasks(needsList, featureFlag?.wireFraudDocumentEnabled),
    [featureFlag?.wireFraudDocumentEnabled, needsList],
  );

  useESignFSTracking(needsList, { trackCreated: true });

  const scheduleStandardNeedsRefetchAfterDelay = useCallback(() => {
    setTimeout(() => {
      void refetchNeedsList?.();
    }, NEEDS_ESIGN_REFETCH_DELAY_MS);
  }, [refetchNeedsList]);

  const safeRefetchNeedsList = useCallback(async () => {
    if (!refetchNeedsList) {
      return undefined;
    }
    return refetchNeedsList();
  }, [refetchNeedsList]);

  const [showWireFraudToast, setShowWireFraudToast] = useState(false);

  const handleWireFraudAcknowledged = useCallback(async () => {
    setShowWireFraudToast(true);
    dispatch(setTaskFilter(TaskFilterType.Complete));
    await safeRefetchNeedsList();
  }, [dispatch, safeRefetchNeedsList]);

  const lastSubmittedNeedIdRef = useRef<string | null>(null);

  const shouldStopNeedsPolling = useCallback((list: NeedItem[], submittedNeedId: string) => {
    const item = list.find((n) => n.id === submittedNeedId);
    if (!item) {
      return false;
    }
    return !isNeedStillInTodoTab(item);
  }, []);

  const handlePollingSuccess = useCallback((id: string) => {
    if (!isEnhancedConditionsReviewSessionEnabled) {
      return;
    }

    const submittedNeed = needsList.find((n) => n.id === id);
    if (submittedNeed?.needType !== NeedType.EnhancedConditions) {
      return;
    }

    dispatch(addSessionHeldUnderReviewId(id));
  }, [dispatch, isEnhancedConditionsReviewSessionEnabled, needsList]);

  const {
    isPolling: isNeedsSubmitPolling,
    isExpectedStatusNotReceived: isNeedsSubmitExpectedStatusNotReceived,
    startPolling: startNeedsSubmitPolling,
    clearExpectedStatusError: clearNeedsSubmitExpectedStatusError,
  } = useNeedsRefetchUntil({
    refetch: safeRefetchNeedsList,
    isFetching: isNeedsListFetching,
    maxAttempts: MAX_NEED_STATUS_REFETCH_ATTEMPTS,
    shouldStop: shouldStopNeedsPolling,
    onSuccess: handlePollingSuccess,
  });

  const showTaskListLoader = isNeedsListLoading || isNeedsSubmitPolling;

  const getHeader = () => {
    const titleClassName = "heading-medium";

    return (
      <div className="tasks__heading">
        <h5 className={titleClassName}>{TASK_CARD_TITLE}</h5>
      </div>
    );
  };

  const getStatusDescription = () => (
    <section className="tasks__subhead" data-testid="tasks-status-description">
      <span className="label-large-regular">{getTaskCardHeadingText(taskFilter)}</span>
    </section>
  );

  const focusSelectedTaskDetail = useCallback(() => {
    const pane = selectedDetailRef.current;
    if (!pane) {
      return;
    }

    const enabledActions = pane.querySelectorAll<HTMLElement>(".task-primary-action:not(:disabled)");
    const action = enabledActions.length > 0 ? enabledActions[enabledActions.length - 1] : null;

    // Mouse-driven programmatic focus does not trigger :focus-visible in Chromium.
    // Mark the target so AC8 focus ring still shows after a selected-row re-click.
    const applyProgrammaticFocusRing = (el: HTMLElement) => {
      el.classList.add("task-primary-action--focused");
      const clearFocusRing = () => {
        el.classList.remove("task-primary-action--focused");
        el.removeEventListener("blur", clearFocusRing);
      };
      el.addEventListener("blur", clearFocusRing);
      el.focus({ preventScroll: true });
    };

    // Defer past the task-row click so the browser does not cancel this scroll
    // when bringing the clicked row into view. Use block:"center" so a footer
    // CTA (Submit) below the fold actually lands in the viewport — "nearest"
    // often no-ops for partially clipped or overflow:hidden ancestors.
    window.requestAnimationFrame(() => {
      if (action) {
        action.scrollIntoView({ behavior: "smooth", block: "center" });
        applyProgrammaticFocusRing(action);
      } else {
        pane.scrollIntoView({ behavior: "smooth", block: "nearest" });
        pane.focus({ preventScroll: true });
      }
    });
  }, []);

  const handleTaskClick = (item: NeedItem) => {
    const isRepeatClick = (isDesktop || isTablet) && item.id === activeTaskId;

    clearNeedsSubmitExpectedStatusError();
    dispatch(setSelectedTaskId(item.id));

    setActiveTaskId(item.id);

    if (!isDesktop) {
      setShowUpload(true);
    }

    if (item.needType === NeedType.Voa) {
      setShowVerificationDrawer(true);
    }

    if (isRepeatClick) {
      focusSelectedTaskDetail();
    }
  };

  useEffect(() => {
    setShowUpload(isDesktop);
  }, [isDesktop]);

  const [activeTaskId, setActiveTaskId] = useState<string | null>(selectedTaskIdFromRedux ?? null);

  const filteredNeedsList = useMemo(() => {
    return displayNeedsList.filter(item => taskFilter === getEffectiveTaskStatus(item, effectiveHeldUnderReviewIds));
  }, [displayNeedsList, taskFilter, effectiveHeldUnderReviewIds]);

  const uniqueErrorTypes = useMemo(() => {
    const errorTypes = needsList
      .filter(item => !!item.taskLoadErrorType)
      .map(item => item.taskLoadErrorType);
    return new Set(errorTypes);
  }, [needsList]);

  const allServicesFailed = useMemo(() => {
    return uniqueErrorTypes.size === 3;
  }, [uniqueErrorTypes]);

  const selectedTask: ActiveTask | null = useMemo(() => {
    if (!activeTaskId) return null;

    const task = displayNeedsList.find(item => item.id === activeTaskId);
    if (!task) return null;

    return {
      needId: task.id,
      title: getTaskDisplayTitle(task),
      assignedTo: task.assignedTo,
      note: task.note,
      needType: task.needType,
      documents: task.documents,
      needStatus: task.needStatus,
      lat: task.lat,
      packageId: task.packageId,
      isEnabled: task.isEnabled,
      prospectId: task.prospectId,
      isEsignStartedFSEventSent: task.isEsignStartedFSEventSent,
      taskLoadErrorType: task.taskLoadErrorType,
      isDefault: task.isDefault,
      needRequestTypeId: task.needRequestTypeId,
      shouldShowInsuranceCta: shouldDisplayNafInsuranceTaskCta(task, featureFlag?.insurancePurchaseBannerEnabled),
      ...taskVOASimulation
    };
  }, [activeTaskId, displayNeedsList, taskVOASimulation, featureFlag?.insurancePurchaseBannerEnabled]);

  const handleDocumentUploadSubmitSuccess = useCallback((submittedFileCount: number) => {
    if (!selectedTask?.needId) {
      return;
    }

    trackFSDocumentsUploaded({
      username,
      timestamp: new Date().toISOString(),
      prospectId: prospectId,
      dealId: dealId || null,
      losLoanNumber: loanNumber || null,
      losId: losId || null,
      numberOfDocumentsUploaded: submittedFileCount,
      loanMilestone: loanProgress?.status ?? "Unknown",
    });

    trackFSGuidedExperienceFirstTaskCompletedIfEligible({
      username,
      prospectId,
      dealId: dealId || null,
      losLoanNumber: loanNumber || null,
    });

    const row = needsList.find((n) => n.id === selectedTask.needId);
    // Additional Documentation (`isDefault`): skip status polling; `updateDocumentIds` invalidates `document` (refetches getNeeds) — optional extra refetch for immediate UI sync.
    if (row?.isDefault !== true) {
      lastSubmittedNeedIdRef.current = selectedTask.needId;
      startNeedsSubmitPolling(selectedTask.needId);
    } 
    // Keep panel open on mobile until user clicks "Back to Tasks"
    if (!isDesktop && !isTablet) {
      setIsMobileSubmitSuccess(true);
    }
  }, [needsList, startNeedsSubmitPolling, selectedTask?.needId, isDesktop, isTablet, username, prospectId, dealId, loanNumber, loanId, loanProgress]);

  // Fallback: if API status never updated after all retries, move the task to Under Review
  // optimistically for this session and suppress the error.
  useEffect(() => {
    if (!isEnhancedConditionsReviewSessionEnabled) {
      return;
    }

    if (isNeedsSubmitExpectedStatusNotReceived && lastSubmittedNeedIdRef.current) {
      const submittedNeed = needsList.find((n) => n.id === lastSubmittedNeedIdRef.current);
      if (submittedNeed?.needType === NeedType.EnhancedConditions) {
        dispatch(addSessionHeldUnderReviewId(lastSubmittedNeedIdRef.current));
      }
      clearNeedsSubmitExpectedStatusError();
      lastSubmittedNeedIdRef.current = null;
    }
  }, [isNeedsSubmitExpectedStatusNotReceived, dispatch, clearNeedsSubmitExpectedStatusError, isEnhancedConditionsReviewSessionEnabled, needsList]);

  const handleSetShowUpload = useCallback((show: boolean) => {
    setShowUpload(show);
    // Reset mobile submit success state when user navigates away
    if (!show) {
      setIsMobileSubmitSuccess(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedTaskIdFromRedux) return;

    setActiveTaskId(selectedTaskIdFromRedux);

    if (!isDesktop) {
      setShowUpload(true);
    }

    dispatch(setSelectedTaskId(undefined));

  }, [selectedTaskIdFromRedux, isDesktop, dispatch]);

    useEffect(() => {
    // On mobile, keep showing the submitted task panel until user clicks "Back to Tasks"
    if (isMobileSubmitSuccess && !isDesktop && !isTablet) {
      return;
    }

    if (!filteredNeedsList.length) {
      setActiveTaskId(null);
      return;
    }

    const exists = filteredNeedsList.some(
      task => task.id === activeTaskId
    );

    if (!exists) {
      setActiveTaskId(filteredNeedsList[0].id);
    }

  }, [filteredNeedsList, activeTaskId, isMobileSubmitSuccess, isDesktop, isTablet]);

  useEffect(() => {
    setTaskVOASimulation({});
  }, [activeTaskId]);

  const updateTaskFilterBadges = () => {
    const updatedFilters = taskFilters.map(filter => {
      const count = displayNeedsList.filter(
        item => getEffectiveTaskStatusForCount(item, effectiveHeldUnderReviewIds) === filter.value
      ).length;
      return {
        ...filter,
        badge: count > 0 ? count.toString() : "",
      };
    });
    setTaskFilters(updatedFilters);
  };

  useEffect(() => {
    updateTaskFilterBadges();
  }, [displayNeedsList, effectiveHeldUnderReviewIds]);

  useEffect(() => {
    if (!isEnhancedConditionsReviewSessionEnabled) {
      return;
    }

    sessionHeldUnderReviewIds?.forEach((id) => {
      const item = needsList.find((n) => n.id === id);
      if (item && isNeedCompleted(item)) {
        dispatch(removeSessionHeldUnderReviewId(id));
      }
    });
  }, [needsList, sessionHeldUnderReviewIds, dispatch, isEnhancedConditionsReviewSessionEnabled]);

  useEffect(() => {
    if (hasInitializedRef.current) {
      return;
    }

    hasInitializedRef.current = true;
    dispatch(setTaskFilter(TaskFilterType.ToDo));
    onLoad?.();
  }, [dispatch, onLoad]);

  const handleClosedVerificationDrawer = () => {
    if (!isDesktop) setShowUpload(false);
    setShowVerificationDrawer(false);
  }

  // To simulate VOA completion, to be removed later
  const handleVerificationSuccess = () => {
    if (selectedTask) {
      setTaskVOASimulation({ ...selectedTask, needStatus: NeedStatus.Completed });
    }
  };

  const hasIndividualErrorTasks = needsList.some(item => !!item.taskLoadErrorType);
  const showComputerErrorIcon = (isNeedsListError || allServicesFailed) && !hasIndividualErrorTasks;

  const renderTaskListBody = () => {
    if (showTaskListLoader) {
      return (
        <div className="tasks__spinner-container">
          <Spinner isApi />
        </div>
      );
    }

    if (showComputerErrorIcon) {
      return (
        <output className="tasks__needs-load-error" aria-live="polite">
          <div className="tasks__needs-load-error-icon">
            <img src={errorWindow} alt="" className="tasks__needs-load-error-window" />
            <img src={errorComputer} alt="" className="tasks__needs-load-error-computer" />
            <img src={errorIconCircle} alt="" className="tasks__needs-load-error-icon-circle" />
            <img src={errorIconExclamation} alt="" className="tasks__needs-load-error-icon-exclamation" />
            <img src={errorStand} alt="" className="tasks__needs-load-error-stand" />
            <img src={errorConnector} alt="" className="tasks__needs-load-error-connector" />
          </div>
          <div className="tasks__needs-load-error-content">
            <h5 className="paragraph-medium-medium">{TASK_NEEDS_LOAD_ERROR_TITLE}</h5>
            <p className="paragraph-medium-regular">
              {TASK_NEEDS_LOAD_ERROR_DESCRIPTION}
              <br />
              {TASK_ERROR_SUPPORT}
            </p>
          </div>
        </output>
      );
    }

    if (isNeedsSubmitExpectedStatusNotReceived) {
      return (
        <output className="tasks__error-state paragraph-medium-medium" aria-live="polite">
          {TASK_STATUS_REFRESH_ERROR}
        </output>
      );
    }

    return (
      <>
        {filteredNeedsList.map((item) => {
          const isSelected = selectedTask?.needId === item.id;
          const hasTaskError = !!item.taskLoadErrorType;
          return (
            <Task
              key={item.id}
              title={getTaskDisplayTitle(item)}
              subTitle={item.assignedTo ?? ""}
              description={isWireFraudTask(item) ? WIRE_FRAUD_TASK_DESCRIPTION : undefined}
              isSelected={isSelected}
              isComments={item.isNewComment || !!item.note?.trim()}
              isError={hasTaskError && item.needType !== NeedType.Voa}
              needType={item.needType}
              needStatus={item.needStatus}
              isDefault={item.isDefault}
              shouldShowInsuranceCta={shouldDisplayNafInsuranceTaskCta(item, featureFlag?.insurancePurchaseBannerEnabled)}
              handleClick={() => {
                handleTaskClick(item);
              }}
            />
          );
        })}
        {isDesktop && filteredNeedsList.length === 0 && (
          <div className="tasks__no-selected-tasks paragraph-medium-medium">
            {taskFilter === TaskFilterType.Complete ? "No completed tasks yet" : "No current tasks"}
          </div>
        )}
      </>
    );
  };

  const renderSelectedTaskContent = () => {
    if (!selectedTask) {
      return null;
    }

    if (selectedTask.taskLoadErrorType && selectedTask.needType !== NeedType.Voa) {
      return (
        <output
          className={`tasks__task-unavailable ${getTaskUnavailableModifierClass(selectedTask)}`}
        >
          {!isDesktop && !isTablet && (
            <button className="back-button" onClick={() => handleSetShowUpload(false)} type="button">
              <img src={chevronLeft} alt="" />
              <p>Back to Tasks</p>
            </button>
          )}
          <div className="tasks__task-unavailable-content">
            <img
              src={getTaskErrorIconSrc(
                isTaskCompletedErrorState(selectedTask),
                isTaskReviewNeeded(selectedTask),
              )}
              alt=""
              aria-hidden="true"
              className="tasks__task-unavailable-icon"
            />
            {renderTaskUnavailableDetails(selectedTask)}
          </div>
        </output>
      );
    }

    if (selectedTask.needType === NeedType.ESign) {
      return (
        <ESignPanel
          status={selectedTask.needStatus || ""}
          needId={selectedTask.needId}
          loanAccessToken={selectedTask.lat}
          packageId={selectedTask.packageId}
          title={selectedTask.title}
          setShowUpload={handleSetShowUpload}
          isEnabled={selectedTask.isEnabled}
          prospectId={selectedTask.prospectId}
          isEsignStartedFSEventSent={selectedTask.isEsignStartedFSEventSent}
          onESignDrawerClosed={scheduleStandardNeedsRefetchAfterDelay}
        />
      );
    }

    if (selectedTask.needType === NeedType.WireFraud) {
      return (
        <WireFraudPanel
          setShowUpload={handleSetShowUpload}
          onAcknowledged={handleWireFraudAcknowledged}
          isCompleted={selectedTask.needStatus?.toLowerCase() === NeedStatus.Completed.toLowerCase()}
        />
      );
    }

    if (selectedTask.needType !== NeedType.Voa) {
      return (
        <FileUploadePanel
          needId={selectedTask.needId || ""}
          needType={selectedTask.needType}
          needRequestTypeId={selectedTask.needRequestTypeId}
          shouldShowInsuranceCta={selectedTask.shouldShowInsuranceCta}
          title={selectedTask.title || ""}
          assignedTo={selectedTask.assignedTo}
          setShowUpload={handleSetShowUpload}
          note={selectedTask.note}
          documents={selectedTask.documents}
          onSubmitSuccess={handleDocumentUploadSubmitSuccess}
        />
      );
    }

    return null;
  };

    return (
    <main className="tasks-main">
      {(isDesktop || !showUpload || isTablet) && (
        <section className={`tasks ${showUpload ? "hidden-mobile" : ""} ${showComputerErrorIcon ? "tasks--full-width" : ""}`}>
          {getHeader()}
          <section className="tasks__current-complete">
            <TogglePills options={taskFilters} value={taskFilter} onChange={(value) => dispatch(setTaskFilter(value))} />
          </section>
          {getStatusDescription()}
          <section className="tasks__task-items">
            {renderTaskListBody()}
          </section>
        </section>
      )}
      {!showComputerErrorIcon && (
        <section className="tasks__selected">
        {filteredNeedsList.length === 0 && (
          <section className="tasks__no-tasks">
            <img src={tasksChecklistAnimation} alt="todo list being checked off with a pencil" />
            <span className="paragraph-medium">{getNoTasksMessage(taskFilter)}</span>
          </section>
        )}
        {(isDesktop || showUpload) && filteredNeedsList.length > 0 && (
          <div
            ref={selectedDetailRef}
            tabIndex={-1}
            className="tasks__selected-detail"
          >
            {renderSelectedTaskContent()}
            {selectedTask?.needType === NeedType.Voa && showVerificationDrawer && (
              <div className="verification-container">
                <Verification
                  verificationType={VerificationType.VOA}
                  closeDrawerForMobile={handleClosedVerificationDrawer}
                  status={selectedTask?.needStatus || ""}
                  handlesuccessfullVerification={handleVerificationSuccess}
                />
              </div>
            )}
          </div>
        )}
        </section>
      )}
      {showWireFraudToast && <WireFraudAcknowledgedToast />}
    </main>
  );
};

export default Tasks;